<template>
  <div>
    <div v-if="loading" class="loading">
      <van-loading color="#B71C1C"/>
      <span>正在加载首页</span></div>
    <boss-home v-else-if="hasBossDashboard" :session="session"/>
    <employee-home v-else/>
  </div>
</template>
<script>
/**
 * Tab 首页入口：先用上次的 session 画出员工首页或老板驾驶舱，再在后台同步。
 * 判断逻辑统一用 @/utils/perm.isBossDashboardAccessible()。
 */
import coreapi from '@/utils/coreapi';
import { isBossDashboardAccessible } from '@/utils/perm';
import { isPageCacheFresh, readPageCache, writePageCache } from '@/utils/pageCache';
import EmployeeHome from './Home';
import BossHome from '@/views/boss/HomeOverview';

function applyIdentity(session) {
  if (!session) return
  localStorage.setItem('CoreEmployeeId', session.employeeId || '');
  localStorage.setItem('CoreEmployeeName', session.employeeName || '');
  localStorage.setItem('CoreDepartmentName', session.departmentName || '');
  localStorage.setItem('CorePositionName', session.positionName || '');
  localStorage.setItem('CorePositionFamily', session.positionFamily || '');
  localStorage.setItem('CoreSupervisor', session.supervisor ? 'true' : 'false');
  localStorage.setItem('CoreBrandPost', session.brandPost || '');
  try {
    localStorage.setItem('CoreIdentityRoles', JSON.stringify(session.identityRoles || []));
    localStorage.setItem('CoreIdentityPermissions', JSON.stringify([...(session.identityPermissions || [])]));
    localStorage.setItem('CoreIdentityDataScopes', JSON.stringify([...(session.identityDataScopes || [])]));
    localStorage.setItem('CoreIdentityUser', localStorage.getItem('UserSysno') || '');
  } catch (_) { /* 兼容老 session 结构 */ }
  localStorage.setItem('CoreIsBoss', isBossDashboardAccessible() ? 'true' : 'false');
}

export default {
  name: 'HomeEntry',
  components: {EmployeeHome, BossHome},
  data() {
    const cached = readPageCache('session')
    if (cached && cached.data) applyIdentity(cached.data)
    const known = !!(cached && cached.data) || !!localStorage.getItem('CorePositionFamily') || !!localStorage.getItem('CoreIsBoss')
    return {
      loading: !known,
      session: cached && cached.data ? cached.data : null
    }
  },
  computed: {
    /** 是否拥有 Boss 驾驶舱权限（统一用共享工具，内部有 manage 通配符 + 角色 + 旧码兼容） */
    hasBossDashboard() {
      return isBossDashboardAccessible();
    }
  },
  mounted() {
    this.resolveHome()
  },
  activated() {
    if (this._booting) return
    if (isPageCacheFresh(readPageCache('session'))) return
    this.resolveHome()
  },
  methods: {
    /** 岗位名启发式推断 positionFamily，与服务端 synchronize 逻辑一致 */
    inferFamily(name) {
      const n = (name || '').toLowerCase();
      if (n.indexOf('总负责人') >= 0 || n.indexOf('董事长') >= 0 || n.indexOf('老板') >= 0 || n === 'boss') return 'GENERAL_MANAGER';
      if (n.indexOf('全栈') >= 0 || n.indexOf('全端开发') >= 0) return 'FULL_STACK';
      if (n.indexOf('销售') >= 0 || n.indexOf('招商') >= 0 || n.indexOf('招生') >= 0) return 'SALES';
      if (n.indexOf('品牌') >= 0 || n.indexOf('运营') >= 0) return 'BRAND';
      if (n.indexOf('财务') >= 0 || n.indexOf('会计') >= 0) return 'FINANCE';
      if (n.indexOf('前台') >= 0 || n.indexOf('接待') >= 0) return 'FRONT_DESK';
      if (n.indexOf('店长') >= 0) return 'REHAB_MANAGER';
      if (n.indexOf('心理') >= 0) return 'PSYCHOLOGICAL_COUNSELOR';
      if (n.indexOf('推拿') >= 0 || n.indexOf('理疗') >= 0) return 'MASSAGE_THERAPIST';
      if (n.indexOf('管理') >= 0) return 'TRAINING_MANAGER';
      return 'GENERAL'
    },
    async resolveHome() {
      if (this._booting) return
      this._booting = true
      const cached = readPageCache('session')
      const known = !!(this.session || (cached && cached.data) || localStorage.getItem('CorePositionFamily'))
      if (cached && cached.data && !this.session) {
        this.session = cached.data
        applyIdentity(cached.data)
      }
      if (known && isPageCacheFresh(cached)) {
        this.loading = false
        this._booting = false
        return
      }
      const positionName = localStorage.getItem('CorePositionName') || '销售';
      const cachedFamily = localStorage.getItem('CorePositionFamily');
      const inferredFamily = this.inferFamily(positionName);
      const positionFamily = !cachedFamily || cachedFamily === 'GENERAL' ? inferredFamily : cachedFamily;
      try {
        const session = await coreapi.synchronize({
          employeeName: localStorage.getItem('CoreEmployeeName') || '当前员工',
          departmentKey: localStorage.getItem('CoreDepartmentKey') || localStorage.getItem('GovDepSysno') || 'DP-SAFE',
          departmentName: localStorage.getItem('CoreDepartmentName') || '安全事业部',
          positionName,
          positionFamily,
          supervisor: localStorage.getItem('isTop') === '1' || localStorage.getItem('CoreSupervisor') === 'true'
        });
        this.session = session
        applyIdentity(session)
        writePageCache('session', session)
        window.dispatchEvent(new CustomEvent('brand-post-updated'));
      } catch (e) {
        if (!known) this.$toast.fail(e.message)
      } finally {
        this.loading = false
        this._booting = false
      }
    }
  }
}
</script>
<style scoped>.loading {
  height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #777;
  font-size: .28rem
}

.loading span {
  margin-top: .2rem
}</style>



// WEBPACK FOOTER //
// src/views/home/HomeEntry.vue